import { MemoryRecoveryStore, type RecoveryRecord, type RecoveryStore } from './recovery-store';

const DB_NAME = 'dark-funnels-recovery';
const DB_VERSION = 1;
const STORE_NAME = 'recovery';

function keyFor(userId: string, funnelId: string): string {
  return `${userId}:${funnelId}`;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open recovery database.'));
  });
}

/**
 * SPEC-007 §20/T040 — native IndexedDB, no wrapper library (REQ-065: no
 * package without a simple native alternative). Every operation is wrapped
 * so a failure here never throws into the caller (FM-006) — it just
 * behaves as "nothing found" / "write silently didn't happen", and
 * `isAvailable()` lets the UI avoid falsely claiming local protection
 * (T044) instead of trusting a write that may not have landed.
 */
export class IndexedDbRecoveryStore implements RecoveryStore {
  private dbPromise: Promise<IDBDatabase> | null = null;
  private broken = false;

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) this.dbPromise = openDatabase();
    return this.dbPromise;
  }

  isAvailable(): boolean {
    return typeof indexedDB !== 'undefined' && !this.broken;
  }

  async get(userId: string, funnelId: string): Promise<RecoveryRecord | null> {
    if (!this.isAvailable()) return null;
    try {
      const db = await this.db();
      return await new Promise<RecoveryRecord | null>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const request = tx.objectStore(STORE_NAME).get(keyFor(userId, funnelId));
        request.onsuccess = () => {
          const row = request.result as ({ key: string } & RecoveryRecord) | undefined;
          resolve(row ? { ...row } : null);
        };
        request.onerror = () => reject(request.error);
      });
    } catch {
      this.broken = true;
      return null;
    }
  }

  async put(record: RecoveryRecord): Promise<void> {
    if (!this.isAvailable()) return;
    try {
      const db = await this.db();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put({ key: keyFor(record.userId, record.funnelId), ...record });
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch {
      this.broken = true;
    }
  }

  async remove(userId: string, funnelId: string): Promise<void> {
    if (!this.isAvailable()) return;
    try {
      const db = await this.db();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).delete(keyFor(userId, funnelId));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch {
      this.broken = true;
    }
  }
}

export interface CreatedRecoveryStore {
  store: RecoveryStore;
  usingFallback: boolean;
}

// A real IndexedDB is one persistent, shared database regardless of how
// many `createRecoveryStore()` callers there are; the in-memory fallback
// must behave the same way within one page session, or a write from one
// caller (e.g. the autosave hook) would be invisible to another (e.g. the
// page's load-time recovery check) even though nothing was "lost".
let memoryFallbackSingleton: MemoryRecoveryStore | null = null;

/** Falls back to an in-memory store (and reports it) when IndexedDB is unusable. */
export function createRecoveryStore(): CreatedRecoveryStore {
  if (typeof indexedDB === 'undefined') {
    if (!memoryFallbackSingleton) memoryFallbackSingleton = new MemoryRecoveryStore();
    return { store: memoryFallbackSingleton, usingFallback: true };
  }
  return { store: new IndexedDbRecoveryStore(), usingFallback: false };
}
